import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  Check,
  Edit2,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { FormField } from "@/components/ui/FormField";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import {
  ExternalSystemNotice,
  isExternalBookingSystem,
} from "@/components/dashboard/ExternalSystemNotice";

type Format = "5v5" | "4v4" | "3v3" | "half-court";
type Currency = "GBP" | "USD" | "CAD" | "AUD" | "NZD";

const COUNTRY_TO_CURRENCY: Record<string, Currency> = {
  GB: "GBP",
  US: "USD",
  CA: "CAD",
  AU: "AUD",
  NZ: "NZD",
};
const CURRENCY_SYMBOL: Record<Currency, string> = {
  GBP: "£",
  USD: "$",
  CAD: "$",
  AUD: "$",
  NZD: "$",
};

interface Court {
  id: string;
  venue_application_id: string;
  name: string;
  format: Format;
  base_price_minor: number | null;
  currency: Currency;
  description: string | null;
  published: boolean;
  created_at: string;
}

/**
 * Venue dashboard → Courts. List, create, edit, publish/unpublish,
 * delete each bookable surface. Every court belongs to the venue
 * admin's own venue_application via RLS; they can't touch anyone
 * else's rows.
 */
export function VenueCourtsTab() {
  const { profile } = useAuth();
  const venueAppId = profile?.venue_application_id;

  const [venueCountry, setVenueCountry] = useState<string | null>(null);
  const [bookingSystem, setBookingSystem] = useState<string | null>(null);
  const [courts, setCourts] = useState<Court[] | null>(null);
  const [editing, setEditing] = useState<Court | "new" | null>(null);

  // Load venue's country (default currency) + booking_system (decides
  // whether this is a Tier 1 venue that should see the external-system
  // notice instead of the manual courts UX).
  useEffect(() => {
    if (!venueAppId) return;
    supabase
      .from("venue_applications")
      .select("country, booking_system")
      .eq("id", venueAppId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error("[VenueCourtsTab] couldn't load venue meta", error);
          setVenueCountry(null);
          setBookingSystem(null);
          return;
        }
        setVenueCountry(data?.country ?? null);
        setBookingSystem(data?.booking_system ?? null);
      });
  }, [venueAppId]);

  async function load() {
    if (!venueAppId) return;
    const { data, error } = await supabase
      .from("courts")
      .select("*")
      .eq("venue_application_id", venueAppId)
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Failed to load courts: " + error.message);
      setCourts([]);
      return;
    }
    setCourts((data ?? []) as Court[]);
  }
  useEffect(() => {
    load();
  }, [venueAppId]);

  async function togglePublish(court: Court) {
    const { error } = await supabase
      .from("courts")
      .update({ published: !court.published })
      .eq("id", court.id);
    if (error) return toast.error(error.message);
    toast.success(court.published ? "Unpublished" : "Published - live in the app");
    await load();
  }

  async function deleteCourt(court: Court) {
    if (!window.confirm(`Delete "${court.name}"? This also deletes all its availability slots.`)) return;
    const { error } = await supabase.from("courts").delete().eq("id", court.id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    await load();
  }

  // Tier 1 venues (external booking system) get a read-only notice
  // instead of the manual courts UI - courts flow from their system
  // automatically once direct sync ships. Mirrors the venue_approved
  // Tier 1 email's "leave the Whistll Courts tab alone" promise.
  if (isExternalBookingSystem(bookingSystem)) {
    const systemName = (bookingSystem ?? "").trim();
    return (
      <div>
        <header className="mb-6">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
            Venue partner · Courts
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">
            Your courts
          </h1>
        </header>
        <ExternalSystemNotice systemName={systemName} variant="courts" />
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
            Venue partner · Courts
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">Your courts</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Each bookable surface - 5v5 hall, 3v3 half-court, etc. Publish
            when ready; draft courts stay hidden from the Whistll app.
          </p>
        </div>
        <Button onClick={() => setEditing("new")}>
          <Plus className="h-4 w-4" />
          Add court
        </Button>
      </header>

      {courts === null ? (
        <PageLoader />
      ) : courts.length === 0 ? (
        <EmptyCourts onAdd={() => setEditing("new")} />
      ) : (
        <ul className="flex flex-col gap-2">
          {courts.map((c) => (
            <CourtRow
              key={c.id}
              court={c}
              onEdit={() => setEditing(c)}
              onToggle={() => togglePublish(c)}
              onDelete={() => deleteCourt(c)}
            />
          ))}
        </ul>
      )}

      {editing && venueAppId && (
        <CourtEditor
          court={editing === "new" ? null : editing}
          venueAppId={venueAppId}
          defaultCurrency={
            venueCountry ? COUNTRY_TO_CURRENCY[venueCountry] : "GBP"
          }
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function EmptyCourts({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/40 py-12 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-volt-subtle text-volt">
        <Building2 className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold">No courts yet</p>
      <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
        Add your first court to start accepting bookings.
      </p>
      <Button className="mt-4" onClick={onAdd}>
        <Plus className="h-4 w-4" />
        Add your first court
      </Button>
    </div>
  );
}

function CourtRow({
  court,
  onEdit,
  onToggle,
  onDelete,
}: {
  court: Court;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const price =
    court.base_price_minor != null
      ? `${CURRENCY_SYMBOL[court.currency]}${(court.base_price_minor / 100).toFixed(2)}`
      : "Variable";
  return (
    <li className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-semibold">{court.name}</h3>
          <span className="rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
            {court.format}
          </span>
          {court.published ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
              <Eye className="h-2.5 w-2.5" /> Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
              <EyeOff className="h-2.5 w-2.5" /> Draft
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {price} per player · {court.currency}
        </p>
        {court.description && (
          <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
            {court.description}
          </p>
        )}
      </div>
      <div className="flex shrink-0 gap-1.5">
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex h-8 items-center gap-1 rounded-md border border-input bg-background px-2 text-xs font-medium hover:bg-accent"
          title={court.published ? "Unpublish" : "Publish"}
        >
          {court.published ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {court.published ? "Unpublish" : "Publish"}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background hover:bg-accent"
          title="Edit"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-destructive hover:bg-destructive/10"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

function CourtEditor({
  court,
  venueAppId,
  defaultCurrency,
  onClose,
  onSaved,
}: {
  court: Court | null;
  venueAppId: string;
  defaultCurrency: Currency;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(court?.name ?? "");
  const [format, setFormat] = useState<Format>(court?.format ?? "5v5");
  const [price, setPrice] = useState(
    court?.base_price_minor != null ? (court.base_price_minor / 100).toFixed(2) : "",
  );
  const [currency, setCurrency] = useState<Currency>(court?.currency ?? defaultCurrency);
  const [description, setDescription] = useState(court?.description ?? "");
  const [saving, setSaving] = useState(false);

  const priceValid = useMemo(() => {
    if (price.trim() === "") return true; // optional
    const n = Number(price);
    return Number.isFinite(n) && n >= 0 && n < 10000;
  }, [price]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required.");
    if (!priceValid) return toast.error("Price looks invalid.");

    setSaving(true);
    const payload = {
      venue_application_id: venueAppId,
      name: name.trim(),
      format,
      base_price_minor:
        price.trim() === "" ? null : Math.round(Number(price) * 100),
      currency,
      description: description.trim() || null,
    };
    const { error } = court
      ? await supabase.from("courts").update(payload).eq("id", court.id)
      : await supabase.from("courts").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(court ? "Saved" : "Court added");
    await onSaved();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="flex w-full max-w-lg flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold tracking-tight">
            {court ? "Edit court" : "New court"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <FormField label="Court name" htmlFor="court-name" required>
          <Input
            id="court-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hall A · 5v5"
            maxLength={80}
          />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Format" htmlFor="court-format" required>
            <select
              id="court-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as Format)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="5v5">5v5</option>
              <option value="4v4">4v4</option>
              <option value="3v3">3v3</option>
              <option value="half-court">Half-court</option>
            </select>
          </FormField>
          <FormField label="Currency" htmlFor="court-currency" required>
            <select
              id="court-currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="GBP">GBP (£)</option>
              <option value="USD">USD ($)</option>
              <option value="CAD">CAD ($)</option>
              <option value="AUD">AUD ($)</option>
              <option value="NZD">NZD ($)</option>
            </select>
          </FormField>
        </div>

        <FormField
          label="Base price per player"
          htmlFor="court-price"
          hint="Leave blank if prices vary per slot. You can still override on each slot."
          error={priceValid ? undefined : "Must be between 0 and 9999."}
        >
          <Input
            id="court-price"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="8.50"
          />
        </FormField>

        <FormField
          label="Description"
          htmlFor="court-description"
          hint="Shown to players in the court browser. Courts, lighting, changing rooms etc."
        >
          <Textarea
            id="court-description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
          />
        </FormField>

        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                {court ? "Save changes" : "Create court"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
